/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import TaskDetail from './taskDetail';
import { hyphenate, taskId } from '../../utils/string';
import { allows } from '../../utils/authorization';
import DropDownMenu from '../Common/dropDownMenu';
import UserInitialsButton from '../Common/userInitialsButton';
import { updateTask } from '../../actions/taskActions';
import { filterWorkersByAuthority } from '../../selectors/userSelector';

class ResultDetailTask extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      taskDetailOpen: false,
      dropDown: {},
      isNameEditable: false,
      task: { ...props.task },
      placeHolderName: props.task.short_name
    };

    this.openTaskDetail = this.openTaskDetail.bind(this);
    this.closeTaskDetail = this.closeTaskDetail.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { task } = prevProps;
    if (task && !isEqual(task, this.props.task)) {
      this.setState({ task: this.props.task, placeHolderName: this.props.task.short_name });
    }
  }

  closeTaskDetail() {
    this.props.closeTaskDetail(this.props.task);
  }

  toggleDropDownOwner(e, oldOwnerId) {
    e.stopPropagation();
    const { task, users, updateTemporaryTaskOwner } = this.props;
    let { dropDown } = this.state;
    if (dropDown.id) {
      dropDown = {};
    } else {
      const { target: element } = e;
      const classes = 'planwall-drop-menu';
      dropDown = {
        task,
        oldOwnerId,
        classes,
        users,
        elementClicked: element,
        elementAttached: element,
        parent:
          element.closest('#scrollable-wall-container') ||
          element.closest('.card-wall'),
        type: 'changeOwner',
        id: task.id || task.temporaryId,
        updateTemporaryTaskOwner,
        closeDropdown: () => this.setState({ dropDown: {} })
      };
    }
    this.setState({ dropDown });
  }

  handleOnChange = (e) => {
    this.setState({ placeHolderName: e.target.value });
  };

  handleOnBlur = ({ target }) => {
    // update name on blur
    const oldName = this.state.task.short_name;
    const newName = target.value;

    if (newName == '') {
      // if user attempts to rename to empty string, reset to old name
      target.value = oldName;
      this.setState({ isNameEditable: false });
      this.setState({ placeHolderName: oldName });
    } else {
      // otherwise update name
      // eslint-disable-next-line no-shadow
      const { task, updateTemporaryTask, updateTask } = this.props;
      if (task.id)
        // undefined if a new task card
        updateTask(task.id, {
          short_name: newName
        });
      else
        updateTemporaryTask(task.temporaryId, {
          short_name: newName
        });

      this.setState({ isNameEditable: false });
    }
  };

  makeNameEditable = () => this.setState({ isNameEditable: true });

  openTaskDetail() {
    this.props.setTaskToEdit(this.props.task);
  }

  render() {
    const {
      task,
      status,
      teams,
      cadences,
      workPackage,
      showTrash,
      taskTypes,
      users
    } = this.props;
    const { dropDown, isNameEditable } = this.state;
    const taskType = taskTypes.find((type) => type.task_id === task.id) || {};
    const intention =
      taskType?.intention || task?.task_type?.intention
        ? hyphenate(taskType.intention || task.task_type.intention)
        : '';

    if (!showTrash && task.deleted_at) return null;

    const deletedClass = task.deleted_at ? 'deleted' : '';
    const blockedClass = task.blocked ? 'blocked-card' : '';
    const completedClass = task.completed ? 'fade' : '';
    const taskCadence = cadences.find(
      (cadence) => cadence.id === task.cadence_id
    );

    const workPackageTeam = teams.find(
      (team) => team.id === workPackage.team_id
    );

    const allowEdit = allows('update', workPackage);

    return (
      <div>
        <div
          className={`panel panel-default task ${deletedClass} ${blockedClass} ${completedClass}`}
        >
          <div className={`panel-heading ${intention}-intention`}>
            <div className="panel-title">
              <div className="col-xs-6">
                {task.work_package_id && task.id && (
                  <a onClick={this.openTaskDetail}>
                    {taskId(task, workPackage.type_id)}
                  </a>
                )}
                {!task.id && <a onClick={this.openTaskDetail}>New Task</a>}
              </div>
              <div className="col-xs-6 text-right">{task.estimate || 0}</div>
            </div>
          </div>
          <div
            className="panel-body"
            onClick={allowEdit ? this.makeNameEditable : null}
          >
            {isNameEditable ? (
              <textarea
                autoFocus={isNameEditable}
                value={this.state.placeHolderName}
                onBlur={this.handleOnBlur}
                onChange={this.handleOnChange}
              />
            ) : (
              <p>{this.state.placeHolderName || task.name}</p>
            )}
          </div>
          <div className="panel-footer">
            <div className="initials">
              {task.task_owner_ids.length === 0 && (
                <button
                  type="button"
                  disabled={!allowEdit || !users || users.length === 0}
                  className="btn btn-link right"
                  onClick={(e) => this.toggleDropDownOwner(e, null)}
                >
                  <div className="initial result-detail">&nbsp;</div>
                </button>
              )}
              {task.task_owner_ids.map((ownerId) => {
                const owner = users.find((user) => user.id === ownerId) || {};
                return (
                  <UserInitialsButton
                    key={`${owner.id}-${task.id}`}
                    className="right"
                    isDisabled={!allowEdit || !users || users.length === 0}
                    onClick={(e) => this.toggleDropDownOwner(e, owner.id)}
                    userId={ownerId}
                    users={users}
                  />
                );
              })}
            </div>
            {workPackageTeam && !workPackageTeam.kanban && (
              <div className={`status-btn ${status} text-center`}>
                {(taskCadence && taskCadence.short_name) || 'NONE'}
              </div>
            )}
          </div>
        </div>
        <TaskDetail
          task={task}
          isOpen={this.state.taskDetailOpen}
          onClose={this.closeTaskDetail}
        />
        {dropDown && dropDown.id && (
          <DropDownMenu
            type={dropDown.type}
            dropDown={dropDown}
            key={dropDown.id}
            disabled={!allowEdit}
          />
        )}
      </div>
    );
  }
}

ResultDetailTask.propTypes = {
  task: PropTypes.object,
  status: PropTypes.string,
  setTaskToEdit: PropTypes.func,
  updateTemporaryTask: PropTypes.func.isRequired
};

const mapStateToProps = (state, ownProps) => {
  const { workPackage } = ownProps;
  const users = workPackage.team_id
    ? filterWorkersByAuthority(state, workPackage)
    : [];
  return {
    showTrash: state.trashReducer.showTrash,
    teams: state.teamReducer.teams,
    cadences: state.cadenceReducer.cadences,
    taskTypes: state.taskTypeReducer.taskTypes,
    users
  };
};

export default connect(mapStateToProps, { updateTask })(ResultDetailTask);
